import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Sparkles, Mail } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { openProCheckout } from "@/lib/paypal-adapter";
import { trackEvent } from "@/lib/analytics";

export default function Pricing() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [billing, setBilling] = useState(null);

  // Waitlist fallback (when PayPal is not yet configured)
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/billing/config").then(({ data }) => setConfig(data)).catch(() => {});
    // Track pricing view (only fires once per mount when user is authenticated)
    trackEvent("pricing_viewed");
  }, []);

  useEffect(() => {
    if (!user) return;
    setEmail(user.email || "");
    api.get("/billing/me").then(({ data }) => setBilling(data)).catch(() => {});
    api.get("/pro/waitlist/me")
      .then(({ data }) => setJoined(!!data.joined))
      .catch(() => {});
  }, [user]);

  const paypalReady = !!config?.configured;
  const isCurrentPro = billing?.plan === "pro" && billing?.has_access;

  const handleUpgrade = async () => {
    if (authLoading) return;
    if (!user) {
      navigate("/signup?next=/pricing");
      return;
    }
    if (isCurrentPro) {
      navigate("/settings");
      return;
    }
    if (!paypalReady) {
      // Waitlist fallback
      return joinWaitlist();
    }
    setCheckoutLoading(true);
    trackEvent("checkout_started", { source: "pricing_page" });
    try {
      await openProCheckout({
        email: user.email,
        userId: user.user_id,
        onCompleted: () => {
          toast.success("Payment received. Activating your Pro plan…");
          setTimeout(() => navigate("/settings?billing=success"), 500);
        },
      });
    } catch (err) {
      toast.error(err?.message || "Could not open checkout");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const joinWaitlist = async () => {
    if (!user) return navigate("/signup");
    setJoining(true);
    try {
      await api.post("/pro/waitlist", { email: email || user.email });
      setJoined(true);
      toast.success("You're on the list. We'll email you when Pro launches.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join the waitlist");
    } finally {
      setJoining(false);
    }
  };

  const freeFeatures = [
    "5 remixes per month",
    "All 5 output formats",
    "8,000 characters per input",
    "Copy & regenerate",
    "History (last 50)",
  ];
  const proFeatures = [
    "Unlimited remixes",
    "40,000 characters per input",
    "Priority generation",
    "All 5 output formats",
    "Full history",
    "Custom Templates",
    "Multi-voice Brand Voice",
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="mx-auto max-w-5xl px-6 py-16 lg:px-10 lg:py-24">
        <div className="mb-16 text-center">
          <p className="mb-4 text-xs uppercase tracking-[0.25em] text-neutral-500">— Pricing</p>
          <h1 className="font-serif text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
            One price. <em className="font-serif text-coral">No surprises.</em>
          </h1>
          <p className="mx-auto mt-6 max-w-lg text-base text-neutral-600">
            Free forever for casual creators. Upgrade to Pro the moment you want to publish every day.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {/* Free */}
          <div
            className="rounded-none border border-neutral-200 bg-white p-10 shadow-soft transition-shadow hover:shadow-soft-hover"
            data-testid="plan-free"
          >
            <div className="mb-8 flex items-baseline justify-between border-b border-neutral-200 pb-6">
              <div>
                <p className="text-xs uppercase tracking-widest text-neutral-500">Starter</p>
                <h3 className="mt-1 font-serif text-3xl font-bold tracking-tight">Free</h3>
              </div>
              <div className="text-right">
                <span className="font-serif text-5xl font-bold">$0</span>
              </div>
            </div>
            <ul className="space-y-3">
              {freeFeatures.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-neutral-700">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.5} />
                  {f}
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              disabled={user && billing?.plan === "free"}
              className="mt-10 h-12 w-full rounded-[3px] border-neutral-300"
              data-testid="plan-free-btn"
              onClick={() => (user ? navigate("/dashboard") : navigate("/signup"))}
            >
              {user ? (billing?.plan === "free" ? "Current plan" : "Downgrade") : "Start free"}
            </Button>
          </div>

          {/* Pro */}
          <div
            className="relative rounded-none border-2 border-coral bg-white p-10 shadow-soft transition-shadow hover:shadow-coral"
            data-testid="plan-pro"
          >
            <span
              className="absolute -top-3 left-8 bg-coral px-3 py-1 text-xs uppercase tracking-widest text-white"
              data-testid="plan-pro-badge"
            >
              {paypalReady ? "Most popular" : "Launching soon"}
            </span>
            <div className="mb-8 flex items-baseline justify-between border-b border-neutral-200 pb-6">
              <div>
                <p className="text-xs uppercase tracking-widest text-coral">Pro</p>
                <h3 className="mt-1 font-serif text-3xl font-bold tracking-tight">Unlimited</h3>
              </div>
              <div className="text-right">
                <span className="font-serif text-5xl font-bold">$19</span>
                <div className="text-xs text-neutral-500">/ month</div>
              </div>
            </div>
            <ul className="space-y-3">
              {proFeatures.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-neutral-700">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-coral" strokeWidth={2} />
                  {f}
                </li>
              ))}
            </ul>

            {isCurrentPro ? (
              <Button
                onClick={() => navigate("/settings")}
                className="mt-10 h-12 w-full rounded-[3px] bg-neutral-900 text-white hover:bg-neutral-800"
                data-testid="plan-pro-current-btn"
              >
                <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Manage in Settings
              </Button>
            ) : paypalReady ? (
              <div className="mt-10 space-y-3">
                <Button
                  onClick={handleUpgrade}
                  disabled={checkoutLoading || authLoading}
                  data-testid="plan-pro-upgrade-btn"
                  className="h-12 w-full rounded-[3px] bg-coral text-white hover:bg-coral-hover hover:shadow-coral"
                >
                  <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  {checkoutLoading ? "Redirecting to PayPal…" : "Start 7-day free trial"}
                </Button>
                <p className="text-center text-xs text-neutral-500">
                  Secure checkout by PayPal. 7-day free trial. Cancel anytime.
                </p>
              </div>
            ) : joined ? (
              <div
                data-testid="waitlist-joined"
                className="mt-10 border border-neutral-200 bg-neutral-50 p-5 text-center"
              >
                <Mail className="mx-auto mb-2 h-5 w-5 text-coral" strokeWidth={1.5} />
                <p className="font-serif text-lg font-semibold">You&apos;re on the list.</p>
                <p className="mt-1 text-xs text-neutral-500">
                  We&apos;ll email <span className="font-medium text-neutral-700">{email || user?.email}</span> the
                  moment Pro launches.
                </p>
              </div>
            ) : (
              <div className="mt-10 space-y-3">
                {user && (
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={user.email}
                    data-testid="waitlist-email-input"
                    className="h-12 rounded-[3px] border-neutral-300 focus-visible:border-coral focus-visible:ring-1 focus-visible:ring-coral"
                  />
                )}
                <Button
                  onClick={handleUpgrade}
                  disabled={joining || authLoading}
                  className="h-12 w-full rounded-[3px] bg-coral text-white hover:bg-coral-hover hover:shadow-coral"
                  data-testid="plan-pro-btn"
                >
                  {authLoading
                    ? "Loading…"
                    : joining
                    ? "Joining…"
                    : user
                    ? "Notify me when Pro launches"
                    : "Get Pro (create account)"}
                </Button>
                <p className="text-center text-xs text-neutral-500">
                  PayPal checkout — you can subscribe now.
                </p>
              </div>
            )}
          </div>
        </div>

        <p className="mt-16 text-center text-xs text-neutral-500">
          Powered by Claude Haiku 4.5. Prices in USD. Billed monthly via PayPal.
        </p>
      </main>
      <Footer />
    </div>
  );
}
